import { useEffect, useRef, useState } from "react"
import { Control, Controller, FieldErrors, FieldValues } from "react-hook-form"
import { FaSquareCheck } from "react-icons/fa6"
import { IoSearch } from "react-icons/io5"
import { LuChevronLeft, LuMoveLeft } from "react-icons/lu"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "react-toastify"

import BackgroundWrapper from "@fuse/sharedComponents/backgroundWrapper"
import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import BlurredLoadingSpinner from "@fuse/sharedComponents/loader/BlurredLoadingSpinner"
import { showErrorToast } from "@fuse/sharedComponents/themeToast/toastUtils"
import FancyText from "@fuse/sharedComponents/typography/FancyText"
import { Question, QUESTIONNAIRES_TYPE } from "@letshang/constants"
import { get } from "lodash-es"
import "slick-carousel/slick/slick-theme.css"
import "slick-carousel/slick/slick.css"

import { CustomDateController } from "@fuse/pages/eventSignUp/BuyTickets/FillQuestionnaire"
import { useFillQuestionnaire } from "@fuse/pages/eventSignUp/BuyTickets/FillQuestionnaire/useFillQuestionnaire"
import Checkbox from "@fuse/pages/eventSignUp/BuyTickets/FillQuestionnaireUpdated/Checkbox"
import PhoneInput from "@fuse/pages/eventSignUp/BuyTickets/FillQuestionnaireUpdated/PhoneInput"
import RadioGroup from "@fuse/pages/eventSignUp/BuyTickets/FillQuestionnaireUpdated/RadioGroup"
import {
  getThePreciseLocation,
  usePredictionAutocomplete,
} from "@fuse/pages/explore/utility/locationFunctions"
import useProfileController from "@fuse/pages/profilePage/useProfileController"

const RenderQuestion = ({
  question,
  index,
  currentIndex,
  errors,
  control,
  form,
}: {
  question: Question
  index: number
  currentIndex: number
  errors: FieldErrors<FieldValues>
  control: Control<FieldValues, any>
  form: any
}) => {
  const questionText = get(question, "question", "")
  const options = get(question, "options", [])
  const inputRefs = useRef<
    Record<number, HTMLTextAreaElement | HTMLInputElement | null>
  >({})

  const inputQuestionTypes = [
    QUESTIONNAIRES_TYPE.LONG_TEXT,
    QUESTIONNAIRES_TYPE.TEXT,
    QUESTIONNAIRES_TYPE.NUMBER,
    QUESTIONNAIRES_TYPE.ADDRESS,
    QUESTIONNAIRES_TYPE.PHONE,
    QUESTIONNAIRES_TYPE.EMAIL,
    QUESTIONNAIRES_TYPE.DATE,
  ]

  const { user } = useProfileController()
  useEffect(() => {
    if (inputRefs.current[currentIndex]) {
      if (question && inputQuestionTypes.includes(question.questionType)) {
        const inputAreaRef = inputRefs.current[currentIndex]
        if (inputAreaRef.value.length) {
          // inputAreaRef?.focus()
          // Focus specific text area
          if (inputAreaRef.type === "textarea") {
            inputAreaRef?.setSelectionRange(
              inputAreaRef.value.length,
              inputAreaRef.value.length,
            ) // Move cursor to the end
          }
        }
      }
    }
  })
  const refs = {
    day: useRef<HTMLInputElement>(null),
    month: useRef<HTMLInputElement>(null),
    year: useRef<HTMLInputElement>(null),
  }
  const locTerm = form[index] || ""

  switch (question.questionType) {
    case QUESTIONNAIRES_TYPE.ADDRESS:
      return AddressInput(
        questionText,
        index,
        control,
        inputRefs,
        errors,
        getThePreciseLocation,
        locTerm,
      )
    case QUESTIONNAIRES_TYPE.DATE:
      return (
        <div className="w-full">
          <div className="flex flex-col w-full items-center justify-start">
            <div className="w-full text-left">
              <p className="text-[#C2C2C280] font-medium text-md">
                {questionText}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 self-center my-2">
            <Controller
              control={control}
              name={`${index}`}
              key={index}
              render={({ field: { onChange, value } }) => (
                <CustomDateController
                  user={user}
                  onChange={onChange}
                  value={value}
                  refs={refs}
                  error={errors[index]}
                  errorClassname={""}
                />
              )}
            />
          </div>
        </div>
      )
    case QUESTIONNAIRES_TYPE.NUMBER:
      return (
        <div className="w-full">
          <div className="flex flex-col w-full items-center justify-start">
            <div className="w-full text-left">
              <p className="text-[#C2C2C280] font-medium text-md">
                {questionText}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 self-center my-2">
            <Controller
              control={control}
              name={`${index}`}
              key={index}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="w-full h-full max-w-lg">
                  <input
                    ref={(el) => (inputRefs.current[index] = el)} // Assign ref dynamically based on index
                    type="number"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder="Enter a number"
                    className="w-full text-md bg-white/10 focus:outline-none focus:ring-0 placeholder-[#C2C2C280] text-white rounded-lg px-4 h-14"
                  />
                </div>
              )}
            />
          </div>

          {errors[index] && (
            <FancyText className="text-md text-textError font-medium">
              {errors[index]?.message?.toString()}
            </FancyText>
          )}
        </div>
      )

    case QUESTIONNAIRES_TYPE.EMAIL:
      return (
        <div className="w-full">
          <div className="flex flex-col w-full items-center justify-start">
            <div className="w-full text-left">
              <p className="text-[#C2C2C280] font-medium text-md">
                {questionText}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 self-center mt-2">
            <Controller
              control={control}
              name={`${index}`}
              defaultValue=""
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="w-full h-full">
                  <textarea
                    ref={(el) => (inputRefs.current[index] = el)} // Assign ref dynamically based on index
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder="Enter email here"
                    style={{ resize: "none" }}
                    className="w-full text-md bg-white/10 focus:outline-none focus:ring-0 placeholder-[#C2C2C280] text-white rounded-lg px-4 pt-4 h-14"
                  />
                </div>
              )}
            />
            {errors[index] && (
              <FancyText className="text-md text-textError font-medium">
                {errors[index]?.message?.toString()}
              </FancyText>
            )}
          </div>
        </div>
      )

    case QUESTIONNAIRES_TYPE.PHONE:
      return (
        <div className="w-full">
          <div className="flex flex-col w-full items-center justify-start">
            <div className="w-full text-left">
              <p className="text-[#C2C2C280] font-medium text-md">
                {questionText}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 self-center mt-2">
            <Controller
              control={control}
              name={`${index}`}
              defaultValue=""
              render={({ field: { onChange, onBlur, value } }) => (
                <PhoneInput
                  control={control}
                  name={`${index}`}
                  onBlur={onBlur}
                  value={value}
                  onChange={onChange}
                  inputRef={(el) => (inputRefs.current[index] = el)}
                />
              )}
            />
            {errors[index] && (
              <FancyText className="text-md text-textError font-medium">
                {errors[index]?.message?.toString()}
              </FancyText>
            )}
          </div>
        </div>
      )

    case QUESTIONNAIRES_TYPE.TEXT:
    case QUESTIONNAIRES_TYPE.LONG_TEXT:
      return (
        <div className="w-full">
          <div className="flex flex-col w-full items-center justify-start">
            <div className="w-full text-left">
              <p className="text-[#C2C2C280] font-medium text-md">
                {questionText}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 self-center mt-2">
            <Controller
              control={control}
              name={`${index}`}
              defaultValue=""
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="w-full h-full">
                  <textarea
                    ref={(el) => (inputRefs.current[index] = el)} // Assign ref dynamically based on index
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder="Write your answer here"
                    style={{ resize: "none" }}
                    className="w-full text-md bg-white/10 focus:outline-none focus:ring-0 placeholder-[#C2C2C280] text-white rounded-lg px-4 pt-4 h-14"
                  />
                </div>
              )}
            />
            {errors[index] && (
              <FancyText className="text-md text-textError font-medium">
                {errors[index]?.message?.toString()}
              </FancyText>
            )}
          </div>
        </div>
      )

    case QUESTIONNAIRES_TYPE.SINGLE_SELECT:
      return (
        <div className="w-full">
          <div className="flex flex-col w-full items-center justify-start">
            <div className="w-full text-left">
              <p className="text-[#C2C2C280] font-medium text-md">
                {questionText}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 self-center mt-2">
            <Controller
              control={control}
              name={`${index}`}
              defaultValue=""
              render={({ field: { onChange, value } }) => {
                return (
                  <RadioGroup
                    options={options}
                    selectedValue={value}
                    onChange={onChange}
                    className="bg-white/10 px-4 py-4 rounded-lg gap-3"
                  />
                )
              }}
            />
            {errors[index] && (
              <FancyText className="text-md text-textError font-medium">
                {errors[index]?.message?.toString()}
              </FancyText>
            )}
          </div>
        </div>
      )

    case QUESTIONNAIRES_TYPE.MULTI_SELECT:
      return (
        <div className="w-full">
          <div className="flex flex-col w-full items-center justify-start">
            <div className="w-full text-left">
              <p className="text-[#C2C2C280] font-medium text-md">
                {questionText}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 self-center mt-2">
            <Controller
              control={control}
              name={`${index}`}
              defaultValue=""
              render={({ field: { onChange, value } }) => {
                return (
                  <Checkbox
                    options={options}
                    selectedValues={value}
                    onChange={onChange}
                    className="bg-white/10 px-4 py-4 rounded-lg gap-3"
                  />
                )
              }}
            />
            {errors[index] && (
              <FancyText className="text-md text-textError font-medium">
                {errors[index]?.message?.toString()}
              </FancyText>
            )}
          </div>
        </div>
      )

    default:
      return null
  }
}

const FillQuestionnaire = () => {
  const navigate = useNavigate()
  const { state } = useLocation()

  const {
    eventId,
    isLoading,
    watch,
    control,
    handleSubmit,
    errors,
    onSubmit,
    flyerImage,
  } = useFillQuestionnaire(state?.invitedId, "fillQuestionnaire")

  const form = watch()

  const handleBackPress = () => {
    navigate(-1)
  }

  const handlePress = () => {
    if (Object.keys(errors).length > 0) {
      showErrorToast(
        "Please ensure all questions are completed before submitting!",
      )
    } else {
      handleSubmit(onSubmit)()
    }
  }

  const Header: React.FC = () => {
    return (
      <div className="absolute top-4 left-4 md:top-12 md:left-12 flex items-center space-x-2 z-20">
        <div
          onClick={handleBackPress}
          className="w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full flex justify-center items-center bg-white/10 cursor-pointer"
        >
          <LuChevronLeft className="text-white text-2xl mr-0.5 md:hidden" />
          <LuMoveLeft className="text-white text-md md:text-2xl hidden md:flex" />
        </div>
      </div>
    )
  }

  return (
    <>
      <BackgroundWrapper bgImage={flyerImage} className="max-h-screen">
        <BlurWrapper
          overlayOpacityDefault="75"
          overlayColorDefault="dark"
          className="min-h-screen"
        >
          <div className="fixed inset-0 w-full h-screen overflow-hidden flex flex-col">
            <Header />
            {eventId && isLoading && <BlurredLoadingSpinner />}

            <div className="flex flex-col items-center justify-center md:justify-start min-h-screen px-4 md:mt-36">
              <div className="flex text-left w-full max-w-sm">
                <FancyText className="text-2.5xl mt-[10px] text-white font-semibold mb-6">
                  Questionnaire
                </FancyText>
              </div>

              <div className="w-full overflow-auto no-scrollbar">
                <div className="text-left w-full max-w-sm mb-6 space-y-4 h-full max-h-[65vh] md:max-h-[60vh] mx-auto">
                  <div className={state?.ticket ? "block" : "hidden"}>
                    <div className="bg-white/10 rounded-lg shadow-md mb-4 false ">
                      <div className="flex flex-row p-4 cursor-pointer items-center">
                        <p className="flex flex-1 text-start text-white text-lg font-semibold">
                          {state?.ticket?.label}
                        </p>
                      </div>
                      <div className="px-4 pb-4">
                        <div className="h-[0.1px] bg-white/10 -mt-2"></div>
                        <div className="flex flex-row gap-2 mt-4">
                          <div className="h-6 w-6 rounded-full bg-gray-600 overflow-hidden">
                            <img
                              src={state?.ticket?.avatarUrl}
                              alt={state?.ticket?.name}
                            />
                          </div>
                          <p className="text-sm text-start text-white">
                            {state?.ticket?.name}
                          </p>
                        </div>
                        <div className="flex flex-col gap-3 mt-4">
                          {state?.ticket?.ticketAddOns
                            ?.filter((item) => item?.isSelected)
                            ?.map((addOn, index: number) => (
                              <div
                                key={index}
                                className="flex flex-row gap-2 cursor-pointer items-center"
                              >
                                <FaSquareCheck className="text-white h-[1.5rem]" />
                                <div className="flex flex-1 flex-col">
                                  <p className="text-start text-sm text-white">
                                    {addOn?.ticketName}
                                  </p>
                                </div>
                                <p className="text-white text-sm">
                                  ${addOn?.ticketPrice}
                                </p>
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-12">
                    {state?.attendeeQuestioners?.map(
                      (question: Question, index: number) => (
                        <div key={index} className="w-full my-4">
                          <RenderQuestion
                            question={question}
                            index={index}
                            currentIndex={index}
                            errors={errors}
                            control={control}
                            form={form}
                          />
                        </div>
                      ),
                    )}
                  </div>
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-white/5">
                <div className="flex items-center justify-center my-8 self-center">
                  <button
                    className="px-6 lg:px-8 min-w-80 bg-white/10 rounded-full border text-white"
                    onClick={handlePress}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </div>
        </BlurWrapper>
      </BackgroundWrapper>
    </>
  )
}

const AddressInput = (
  questionText: string,
  index: number,
  control: Control<FieldValues, any>,
  inputRefs: React.MutableRefObject<
    Record<number, HTMLTextAreaElement | HTMLInputElement | null>
  >,
  errors: FieldErrors<FieldValues>,
  getThePreciseLocation: (
    callback: (location: { location: { nameOfPlace: string } }) => void,
  ) => void,
  locTerm: string,
) => {
  const [showPredictions, setShowPredictions] = useState(false)
  const [isUserTyping, setIsUserTyping] = useState(false)
  const { predictionList } = usePredictionAutocomplete({
    locTerm,
    isSessionRequired: true,
  })

  const sanitizeInput = (input: string): string => {
    // Replace potentially dangerous characters
    return input
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")
  }

  return (
    <div className="w-full">
      <div className="flex flex-col w-full items-center justify-start">
        <div className="w-full text-left">
          <p className="text-[#C2C2C280] font-medium text-md">{questionText}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 self-center mt-2">
        <Controller
          control={control}
          name={`${index}`}
          defaultValue=""
          render={({ field: { onChange, onBlur, value } }) => {
            const handlePredictionClick = (suggestion: any) => {
              setShowPredictions(false)
              onChange(sanitizeInput(suggestion?.description || ""))
            }

            return (
              <div className="w-full h-full">
                <textarea
                  ref={(el) => (inputRefs.current[index] = el)}
                  value={value}
                  onChange={(e) => {
                    setIsUserTyping(true)
                    setShowPredictions(true)
                    onChange(sanitizeInput(e.target.value))
                  }}
                  onBlur={() => {
                    // Add a small delay to allow click events to process first
                    setTimeout(() => {
                      setIsUserTyping(false)
                      onBlur()
                    }, 200)
                  }}
                  placeholder="Enter location"
                  style={{ resize: "none" }}
                  className="w-full text-md bg-white/10 focus:outline-none focus:ring-0 placeholder-[#C2C2C280] text-white rounded-lg px-4 pt-4 h-14"
                />

                <button
                  onClick={() => {
                    getThePreciseLocation((location) => {
                      onChange(location?.location?.nameOfPlace)
                    })
                  }}
                  className="flex items-center gap-3 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg shadow-md w-full transition-all"
                >
                  <IoSearch color="#ffffff80" size={20} />
                  <div className="flex flex-col text-left">
                    <span className="text-md font-medium">
                      Detect the location
                    </span>
                    <span className="text-xs text-white/50">Using GPS</span>
                  </div>
                </button>

                {showPredictions &&
                  isUserTyping &&
                  predictionList.length > 0 && (
                    <ul className="top-full left-0 w-full bg-white/10 text-white text-md font-regular rounded-md max-h-40 my-5 overflow-y-auto z-10">
                      {predictionList.map((suggestion, i) => (
                        <li
                          key={i}
                          className="p-2 cursor-pointer hover:bg-white/20"
                          onClick={() => handlePredictionClick(suggestion)}
                        >
                          {suggestion?.description}
                        </li>
                      ))}
                    </ul>
                  )}
              </div>
            )
          }}
        />
      </div>

      {errors[index] && (
        <FancyText className="text-[16px] md:text-[24px] text-textError font-[500] text-start w-full">
          {errors[index]?.message?.toString()}
        </FancyText>
      )}
    </div>
  )
}

export default FillQuestionnaire
